import React from 'react';
import cn from '@/utils/cn';
import {
  EMPTY_STATE_CLASS, ForensicsLink, LoadingSkeleton, SortableTh, TABLE_WRAPPER_CLASS, TH_CLASS,
  buildRowClass, formatDuration, formatElapsed,
} from './tableShared';
import type { EndReason, RecentlyEndedSession, SortState } from './types';

interface EndedSessionsTableProps {
  isLoading: boolean;
  onSelectSession?: (sessionId: string) => void;
  onSort?: (key: string) => void;
  selectedSessionId?: string | null;
  sessions: RecentlyEndedSession[];
  sort?: SortState;
}

const END_REASON_LABEL: Record<EndReason, string> = {
  error: 'Error',
  teacher_ended: 'Teacher ended',
  timeout: 'Timeout',
};

const END_REASON_CLASS: Record<EndReason, string> = {
  error: 'tw-bg-red-100 tw-text-red-700 tw-border-red-200',
  teacher_ended: 'tw-bg-gray-100 tw-text-gray-700 tw-border-gray-200',
  timeout: 'tw-bg-yellow-100 tw-text-yellow-800 tw-border-yellow-200',
};

const EndReasonBadge = ({ reason }: { reason: EndReason | null }) => {
  if (!reason) return <span className="tw-text-gray-400 tw-text-xs">—</span>;
  return (
    <span
      className={cn(
        'tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium',
        'tw-border tw-border-solid',
        END_REASON_CLASS[reason],
      )}
    >
      {END_REASON_LABEL[reason]}
    </span>
  );
};

const EndedSessionsTable = ({
  isLoading,
  onSelectSession,
  onSort,
  selectedSessionId = null,
  sessions,
  sort,
}: EndedSessionsTableProps) => {
  if (isLoading) return <LoadingSkeleton />;

  if (sessions.length === 0) {
    return <div className={EMPTY_STATE_CLASS}>No sessions ended in the selected window.</div>;
  }

  const renderSortable = (key: string, label: string) => {
    if (!sort || !onSort) return <th className={TH_CLASS}>{label}</th>;
    return (
      <SortableTh
        active={sort.key === key}
        direction={sort.dir}
        label={label}
        sortKey={key}
        onSort={onSort}
      />
    );
  };

  return (
    <div className={TABLE_WRAPPER_CLASS}>
      <table className="tw-w-full tw-border-collapse">
        <thead className="tw-bg-gray-50">
          <tr>
            {renderSortable('teacher', 'Teacher')}
            {renderSortable('program', 'Program')}
            {renderSortable('slide_show', 'Slideshow')}
            {renderSortable('ended_at', 'Ended')}
            {renderSortable('duration', 'Duration')}
            {renderSortable('end_reason', 'End reason')}
            <th className={TH_CLASS}>Joined</th>
            <th className={TH_CLASS}><span className="tw-sr-only">Forensics</span></th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((session) => {
            const onClick = onSelectSession ? () => onSelectSession(session.session_id) : undefined;
            const isSelected = selectedSessionId === session.session_id;
            return (
              <tr
                key={session.session_id}
                aria-selected={isSelected || undefined}
                className={buildRowClass({ isSelected, onClick })}
                onClick={onClick}
              >
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900">
                  {session.teacher.name ?? '—'}
                </td>
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700">
                  {session.program.name ?? '—'}
                </td>
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700">
                  {session.slide_show.name ?? '—'}
                </td>
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700 tw-whitespace-nowrap">
                  {formatElapsed(session.ended_at)} ago
                </td>
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700 tw-whitespace-nowrap">
                  {formatDuration(session.duration_seconds)}
                </td>
                <td className="tw-px-4 tw-py-3">
                  <EndReasonBadge reason={session.end_reason} />
                </td>
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900">
                  {session.participation_count.toLocaleString()}
                </td>
                <td className="tw-px-4 tw-py-3">
                  <ForensicsLink eventsCount={session.events_count} sessionId={session.session_id} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default EndedSessionsTable;
